import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { plainTextToLexicalSync } from '@plato/ui-legacy/lib/rich-text/plain-text-to-lexical';
import {
    Collapsible,
    CollapsibleHeader,
    CollapsibleHeading,
    CollapsiblePanel,
    CollapsibleTitle,
    CollapsibleTrigger,
} from '@plato/ui/containers/collapsible';
import { PlusIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CrmTaskInputDTO, CrmTaskIntent } from '../../../server/dto/crm-task-input-dto.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { useLogInteractionDialog } from '../log-interaction-dialog-provider.tsx';
import { TaskBulkDialog } from './log-interaction-task-bulk-dialog.tsx';
import { LogInteractionTaskCard } from './log-interaction-task-card.tsx';

export function LogInteractionTaskList(): ReactNode {
    const { t } = useTranslation('notes');
    const { customerId, customerName, stagedTasks, addTasks } = useLogInteractionDialog();

    const openCreateTask = (description: string, taskIntent: CrmTaskIntent): void => {
        const initialValues: Partial<CrmTaskInputDTO> = {
            description: description ? (plainTextToLexicalSync(description) ?? undefined) : undefined,
            taskIntent,
            links: [
                {
                    modelType: SearchEntity.Customer,
                    modelId: customerId,
                    title: customerName ?? null,
                },
            ],
        };

        void DialogStateManager.show(TaskBulkDialog, {
            initialValues,
            onSubmit: (tasks) => {
                addTasks(tasks, 'tasks_section');
            },
        });
    };

    return (
        <Collapsible>
            <CollapsibleHeader>
                <CollapsibleHeading>
                    <CollapsibleTitle>{t('notes__tasks_section_title')}</CollapsibleTitle>
                </CollapsibleHeading>
                <CollapsibleTrigger />
            </CollapsibleHeader>
            <CollapsiblePanel>
                <div className="flex gap-2">
                    <Button
                        type="button"
                        variant="subtle"
                        rounded
                        leftIcon={PlusIcon}
                        className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                        onClick={() => {
                            openCreateTask(t('notes__tasks_quick_action_call'), 'CALL');
                        }}
                    >
                        {t('notes__tasks_quick_action_call')}
                    </Button>
                    <Button
                        type="button"
                        variant="subtle"
                        rounded
                        leftIcon={PlusIcon}
                        className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                        onClick={() => {
                            openCreateTask(t('notes__tasks_quick_action_clarify_price'), 'CLARIFY_PRICE');
                        }}
                    >
                        {t('notes__tasks_quick_action_clarify_price')}
                    </Button>
                    <Button
                        type="button"
                        variant="subtle"
                        rounded
                        leftIcon={PlusIcon}
                        className="h-auto gap-1.5 self-start bg-neutral-100 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-200"
                        onClick={() => {
                            openCreateTask('', 'GENERIC');
                        }}
                    >
                        {t('notes__tasks_quick_action_task')}
                    </Button>
                </div>
                <div className="flex flex-col gap-2 pt-2">
                    {stagedTasks.map((item) => (
                        <LogInteractionTaskCard key={item.key} item={item} />
                    ))}
                </div>
            </CollapsiblePanel>
        </Collapsible>
    );
}
